import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAdminVenues } from "@/hooks/use-admin-venues";
import {
  useSuspendVenue,
  useUnsuspendVenue,
  useDeleteVenue,
} from "@/hooks/use-admin-venue-actions";
import { useVenueSwitcher } from "@/contexts/venue-switcher-context";

export default function VenueDetailPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const { data: venues = [], isLoading } = useAdminVenues();
  const { startImpersonating } = useVenueSwitcher();
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const suspendMutation = useSuspendVenue();
  const unsuspendMutation = useUnsuspendVenue();
  const deleteMutation = useDeleteVenue();
  const venue = venues.find((v) => v.id === venueId);

  if (isLoading) {
    return (
      <div>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="mb-4 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Customers
            </button>
            <div className="h-8 w-48 rounded animate-pulse bg-muted" />
          </div>
        </div>

        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-6 w-32 rounded animate-pulse bg-muted" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 w-full rounded animate-pulse bg-muted" />
                  <div className="h-4 w-2/3 rounded animate-pulse bg-muted" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div>
        <button
          onClick={() => navigate("/admin")}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Customers
        </button>
        <Card className="border-border/50">
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-muted-foreground">Venue not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getMRR = (tier: string | null | undefined): string => {
    if (!tier) return "$0/mo";
    const tierLower = tier.toLowerCase();
    if (tierLower === "free") return "$0/mo";
    if (tierLower === "basic") return "$40/mo";
    if (tierLower === "pro") return "$99/mo";
    return "$0/mo";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleSwitchVenue = () => {
    startImpersonating(venue.id, venue.name);
    navigate("/app");
  };

  return (
    <div>
      <button
        onClick={() => navigate("/admin")}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Customers
      </button>

      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{venue.name}</h1>
          <div className="mt-2 flex items-center gap-3">
            <Badge
              className={`text-xs font-medium ${
                venue.status === "active"
                  ? "bg-accent/20 text-accent"
                  : venue.status === "suspended"
                  ? "bg-destructive/20 text-destructive"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {venue.status === "active" ? "Active" : venue.status === "suspended" ? "Suspended" : "Inactive"}
            </Badge>
          </div>
        </div>
        <Button
          onClick={handleSwitchVenue}
          variant="outline"
          className="bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800"
        >
          <ExternalLink className="h-4 w-4 mr-2" /> Switch to this venue
        </Button>
      </div>

      <div className="mb-8 flex items-center gap-3">
        {venue.status === "active" && (
          <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
            <Button
              onClick={() => setShowSuspendDialog(true)}
              variant="destructive"
              disabled={suspendMutation.isPending}
            >
              {suspendMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Suspend Venue
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Suspend {venue.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Users will still be able to log in but won't be able to create or edit anything.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex gap-3 justify-end">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    await suspendMutation.mutateAsync(venue.id);
                    setShowSuspendDialog(false);
                  }}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Suspend
                </AlertDialogAction>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {venue.status === "suspended" && (
          <Button
            onClick={async () => {
              await unsuspendMutation.mutateAsync(venue.id);
            }}
            variant="outline"
            disabled={unsuspendMutation.isPending}
          >
            {unsuspendMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Unsuspend Venue
          </Button>
        )}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <Button
            onClick={() => setShowDeleteDialog(true)}
            variant="destructive"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Delete Venue
          </Button>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {venue.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this venue and all its data including leaderboards, entries, and subscriptions. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex gap-3 justify-end">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  await deleteMutation.mutateAsync(venue.id);
                  setShowDeleteDialog(false);
                  navigate("/admin");
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Venue Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                <span className="text-sm font-medium text-muted-foreground">Venue ID</span>
                <span className="text-sm font-mono text-foreground">{venue.id}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                <span className="text-sm font-medium text-muted-foreground">Slug</span>
                <span className="text-sm font-mono text-foreground">{venue.slug}</span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                <span className="text-sm font-medium text-muted-foreground">Owner ID</span>
                <span className="text-sm font-mono text-foreground">{venue.ownerId}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {venue.subscription ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Subscription & Billing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">Tier</span>
                  <Badge className="bg-primary/20 text-primary text-xs font-medium">
                    {venue.subscription.tier}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">Status</span>
                  <Badge
                    className={`text-xs font-medium ${
                      venue.subscription.status === "active"
                        ? "bg-accent/20 text-accent"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {venue.subscription.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">MRR</span>
                  <span className="text-sm font-mono font-semibold text-foreground">
                    {getMRR(venue.subscription.tier)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">Next Billing</span>
                  <span className="text-sm font-mono text-foreground">
                    {formatDate(venue.subscription.currentPeriodEnd)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-4 py-3">
                  <span className="text-sm font-medium text-muted-foreground">Stripe Subscription ID</span>
                  <span className="text-sm font-mono text-foreground">
                    {venue.subscription.stripeSubscriptionId.substring(0, 8)}...
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Subscription & Billing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border/50 bg-muted/30 px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Free tier — no active subscription
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Active Campaigns</p>
              <p className="text-4xl font-bold text-primary">{venue.activeCampaignCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
